import { Status } from '@admin/components/helpers/error_icon';

import { DispatcherDispatchRecord, FieldShiftAssignmentRecord } from './dispatcher_types';

type VehicleType = {
  id: number | string;
  num_seats?: number;
  cuft?: number;
};

type DispatcherUser = {
  id: number | string;
  roles: string[];
};

type ValidationContext = {
  vehicleTypes: VehicleType[];
  users: DispatcherUser[];
  fieldShiftAssignments: FieldShiftAssignmentRecord[];
};

const assignmentsFor = (dispatch: DispatcherDispatchRecord) =>
  (dispatch.assignments as Array<{
    role?: string;
    _destroy?: boolean;
    user?: { id: number | string };
  }>) ?? [];

const ordersFor = (dispatch: DispatcherDispatchRecord) =>
  (dispatch.orders as Array<{
    movers?: number;
    service_type?: string;
    parent_id?: number | null;
    type?: string;
    address?: { addressable_type?: string };
  }>) ?? [];

const vehiclesFor = (dispatch: DispatcherDispatchRecord) => dispatch.vehicles ?? [];

const vehiclePreferencesFor = (dispatch: DispatcherDispatchRecord) =>
  (dispatch.vehicle_preferences as Array<{
    vehicle_type_id: number | string;
    quantity?: number;
    _destroy?: boolean;
  }>) ?? [];

const vehicleRecipeFor = (dispatch: DispatcherDispatchRecord) =>
  dispatch.vehicle_recipe as {
    max_quantity?: number;
    min_quantity?: number;
    minimum_cuft?: number;
    allowed_vehicle_type_ids?: number[];
  } | null;

const isWarehouseOrder = (order: ReturnType<typeof ordersFor>[number]) =>
  order.address?.addressable_type === 'Warehouse' && order.type !== 'restock' && order.service_type !== 'drop_off';

const inefficientStaffing = (dispatch: DispatcherDispatchRecord) => {
  const activeAssignments = assignmentsFor(dispatch).filter(({ role, _destroy }) => !_destroy && role !== 'trainee');
  const overStaffableOrders = ordersFor(dispatch).filter(({ service_type }) => service_type === 'curbside_pickup');

  return overStaffableOrders.some(({ movers }) => (movers ?? 0) < activeAssignments.length);
};

const overstaffed = (dispatch: DispatcherDispatchRecord) => {
  const activeAssignments = assignmentsFor(dispatch).filter(({ role, _destroy }) => !_destroy && role !== 'trainee');
  const maxMovers = Math.max(...ordersFor(dispatch).map((order) => order.movers ?? 1), 1);

  return activeAssignments.length > maxMovers;
};

const wrongDriverCount = (dispatch: DispatcherDispatchRecord) => {
  const drivingAssignmentsCount = assignmentsFor(dispatch).filter(
    ({ role, _destroy }) => !_destroy && (role === 'lead' || role === 'driver'),
  ).length;
  const vehicles = vehiclesFor(dispatch);
  const vehicleRecipe = vehicleRecipeFor(dispatch);

  if (vehicles.length > 0) {
    return vehicles.length !== drivingAssignmentsCount;
  }

  if (vehicleRecipe?.max_quantity) {
    return vehicleRecipe.max_quantity !== drivingAssignmentsCount;
  }

  return drivingAssignmentsCount !== 1;
};

const doubleBookedWarnings = (
  dispatch: DispatcherDispatchRecord,
  fieldShiftAssignments: FieldShiftAssignmentRecord[],
) => {
  if (dispatch.id == null) {
    return [];
  }

  return fieldShiftAssignments
    .filter((assignment) => {
      const dispatchIds = (assignment.dispatch_ids as Array<string | number> | undefined) ?? [];
      return dispatchIds.some((id) => String(id) === String(dispatch.id));
    })
    .map((assignment) => assignment.multiple_dispatches_warning as string)
    .filter((warning): warning is string => Boolean(warning));
};

const isIncompleteVehicleRequest = (dispatch: DispatcherDispatchRecord) => {
  const vehiclePreferences = vehiclePreferencesFor(dispatch);
  const vehicleRecipe = vehicleRecipeFor(dispatch);

  return (
    !vehicleRecipe ||
    vehiclePreferences.length === 0 ||
    vehiclePreferences.every((preference) => !preference.quantity || preference.quantity <= 0) ||
    !vehicleRecipe.max_quantity ||
    !vehicleRecipe.min_quantity ||
    !vehicleRecipe.minimum_cuft ||
    !vehicleRecipe.allowed_vehicle_type_ids ||
    vehicleRecipe.allowed_vehicle_type_ids.length === 0
  );
};

const numPreferredVehicles = (dispatch: DispatcherDispatchRecord) =>
  vehiclePreferencesFor(dispatch).reduce((total, preference) => {
    if ((preference.quantity ?? 0) > 0) {
      return total + (preference.quantity ?? 0);
    }
    return total;
  }, 0);

const seatsAvailable = (dispatch: DispatcherDispatchRecord, vehicleTypes: VehicleType[]) =>
  vehiclePreferencesFor(dispatch).reduce((total, preference) => {
    const vehicleType = vehicleTypes.find((type) => type.id === preference.vehicle_type_id);
    if (!preference._destroy && (preference.quantity ?? 0) > 0 && vehicleType) {
      return total + (preference.quantity ?? 0) * (vehicleType.num_seats ?? 0);
    }
    return total;
  }, 0);

const cuftUsed = (dispatch: DispatcherDispatchRecord, vehicleTypes: VehicleType[]) =>
  vehiclePreferencesFor(dispatch).reduce((total, preference) => {
    const vehicleType = vehicleTypes.find((type) => type.id === preference.vehicle_type_id);
    if (!preference._destroy && (preference.quantity ?? 0) > 0 && vehicleType) {
      return total + (preference.quantity ?? 0) * (vehicleType.cuft ?? 0);
    }
    return total;
  }, 0);

const mismatchedVehiclePreferenceRecipe = (dispatch: DispatcherDispatchRecord, vehicleTypes: VehicleType[]) => {
  const vehiclePreferences = vehiclePreferencesFor(dispatch);
  const vehicleRecipe = vehicleRecipeFor(dispatch);

  if (!vehicleRecipe) {
    return false;
  }

  const mismatchedPreferenceVehicleTypes = vehiclePreferences.some(
    (preference) =>
      !preference._destroy &&
      (preference.quantity ?? 0) > 0 &&
      !vehicleRecipe.allowed_vehicle_type_ids?.includes(Number(preference.vehicle_type_id)),
  );

  return (
    cuftUsed(dispatch, vehicleTypes) < (vehicleRecipe.minimum_cuft ?? 0) ||
    numPreferredVehicles(dispatch) > (vehicleRecipe.max_quantity ?? 0) ||
    numPreferredVehicles(dispatch) < (vehicleRecipe.min_quantity ?? 0) ||
    mismatchedPreferenceVehicleTypes
  );
};

export const dispatchHasWarehouseOrder = (dispatch: DispatcherDispatchRecord) =>
  ordersFor(dispatch).some(isWarehouseOrder);

export const moverCountErrors = (dispatch: DispatcherDispatchRecord, context: ValidationContext) => {
  const errors: { message: string; status: Status }[] = [];

  if (inefficientStaffing(dispatch)) {
    errors.push({
      message: 'Too many movers assigned to a route with non Pickup & Packing orders.',
      status: Status.Fair,
    });
  }

  if (overstaffed(dispatch)) {
    errors.push({ message: 'Too many movers assigned to the route.', status: Status.Bad });
  }

  if (wrongDriverCount(dispatch)) {
    const message =
      vehiclesFor(dispatch).length > 0
        ? 'The number of movers with a driving role (leads plus drivers) must be equal to the number of vehicles.'
        : 'The number of movers with a driving role (leads plus drivers) must be equal to the maximum number of vehicles required.';
    errors.push({ message, status: Status.Bad });
  }

  doubleBookedWarnings(dispatch, context.fieldShiftAssignments).forEach((warning) => {
    errors.push({ message: warning, status: Status.Fair });
  });

  return errors;
};

export const vehicleRequestErrors = (dispatch: DispatcherDispatchRecord, context: ValidationContext) => {
  const errors: { message: string; status: Status }[] = [];

  if (isIncompleteVehicleRequest(dispatch)) {
    errors.push({
      message: 'Incomplete Vehicle Request. Please complete all fields in the Vehicle Request.',
      status: Status.Bad,
    });
  }

  const userMap = context.users.reduce<Record<string | number, DispatcherUser>>((map, user) => {
    map[user.id] = user;
    return map;
  }, {});

  const activeAssignments = assignmentsFor(dispatch).filter(({ _destroy }) => !_destroy);

  const driverCount = activeAssignments.reduce((total, assignment) => {
    if (!assignment.user) {
      return total;
    }

    const user = userMap[assignment.user.id];
    if (user?.roles.includes('driver')) {
      return total + 1;
    }

    return total;
  }, 0);

  const vehiclePreferences = dispatch.vehicle_preferences;

  if (!vehiclePreferences || driverCount < numPreferredVehicles(dispatch)) {
    errors.push({ message: 'Not enough drivers for the number of vehicles requested.', status: Status.Bad });
  }

  if (!vehiclePreferences || seatsAvailable(dispatch, context.vehicleTypes) < activeAssignments.length) {
    errors.push({
      message: 'Not enough seats in the vehicles requested for the number of movers assigned.',
      status: Status.Bad,
    });
  }

  if (dispatch.load_out == null) {
    errors.push({ message: 'No material load out selected.', status: Status.Bad });
  }

  if (
    vehicleRecipeFor(dispatch) &&
    vehiclePreferences &&
    mismatchedVehiclePreferenceRecipe(dispatch, context.vehicleTypes)
  ) {
    errors.push({ message: 'Vehicle Preferences does not satisfy Vehicle Requirements.', status: Status.Bad });
  }

  return errors;
};

export const vehicleRequestIconStatus = (dispatch: DispatcherDispatchRecord, context: ValidationContext) =>
  vehicleRequestErrors(dispatch, context).reduce(
    (worstStatus, { status }) => Math.max(worstStatus, status),
    Status.Good,
  );

export const vehicleRequestErrorMessages = (dispatch: DispatcherDispatchRecord, context: ValidationContext) =>
  vehicleRequestErrors(dispatch, context).map(({ message }) => message);
